import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Divider } from '@telus-uds/components-web';

import Role from './fields/role';
import FirstName from './fields/firstName';
import LastName from './fields/lastName';
import Email from './fields/email';
import Contact from './fields/contact';
import FormActionButtons from './fields/button';
import { CONSTANT } from '../../../../utils/constant';
import { FormContainer } from './css';
import { getFields } from './util';
import AccessControl from './accessControl';
import TwoFactorAuthentication from './2fa';
import DealershipVisibility from './fields/dealershipVisibility';

export default function UserDetailsForm({ userData, fetchUsers }) {
  const {
    KEYS: {
      ROLES: {
        ADMIN_TIME_TRACKING,
        TEAM_MEMBER_TIME_TRACKING,
        MANAGER_TIME_TRACKING,
        SALES_MANAGER
      },
      REQUIRES_ORDER_ACCESS: { NO: REQUIRES_ORDER_ACCESS_NO }
    }
  } = CONSTANT;
  const {
    role,
    first_name: firstName,
    last_name: lastName,
    email,
    contact_number: contactNumber,
    provinces,
    channels,
    outlet,
    cpms_id: cpmsId,
    store_code: storeCode,
    Dealership: dealership,
    channelOrgCode,
    sales_manager: salesManager,
    sales_manager_email: salesManagerEmail,
    xid,
    formTypes,
    isAuthDisabled,
    requiresOrderAccess,
    dealershipVisibilityEnabled,
    id
  } = userData;

  const initialState = {
    id,
    role: role.name,
    firstName,
    lastName,
    email,
    contactNumber,
    provinces: provinces.map(province => province.name),
    channels: channels.map(channel => channel.name),
    outlet,
    cpmsId,
    xid,
    storeCode,
    dealership: dealership ? dealership.id : '',
    channelOrgCode,
    salesManager,
    salesManagerEmail,
    isAuthDisabled: isAuthDisabled || false,
    // SOF-3356 — when an admin opens an SM profile that has no
    // requiresOrderAccess persisted yet, default the dropdown to "No" so the
    // value is visibly preselected and gets persisted on save (rather than
    // showing a blank placeholder). Non-SM roles still default to '' because
    // buildUpdatePayload (T021) clears the field for non-SM roles before
    // submission, so this branch has no effect for SR/Admin/etc.
    requiresOrderAccess:
      requiresOrderAccess ||
      (role.name === SALES_MANAGER ? REQUIRES_ORDER_ACCESS_NO : ''),
    dealershipVisibilityEnabled: dealershipVisibilityEnabled || false
  };

  const [fieldData, setFieldData] = useState(initialState);
  const [formsControl, setFormsControl] = useState(formTypes);
  const [formErrors, setFormErrors] = useState({});

  return (
    <FormContainer>
      <TwoFactorAuthentication
        fieldData={fieldData}
        setFieldData={setFieldData}
      />
      <Divider space={0} variant={{ inverse: true }} />
      <Role
        fieldData={fieldData}
        setFieldData={setFieldData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
      />
      <FirstName
        fieldData={fieldData}
        setFieldData={setFieldData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
      />
      <LastName
        fieldData={fieldData}
        setFieldData={setFieldData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
      />
      <Email fieldData={fieldData} />
      <Contact
        fieldData={fieldData}
        setFieldData={setFieldData}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
      />
      {getFields(fieldData).map(FieldComponent => (
        <FieldComponent
          fieldData={fieldData}
          setFieldData={setFieldData}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
        />
      ))}
      {![
        ADMIN_TIME_TRACKING,
        TEAM_MEMBER_TIME_TRACKING,
        MANAGER_TIME_TRACKING
      ].includes(role.name) && (
        <>
          {/* SOF-3356 T031 — Admin-only checkbox above Available Forms. */}
          <DealershipVisibility
            fieldData={fieldData}
            setFieldData={setFieldData}
          />
          <AccessControl
            formsControl={formsControl}
            setFormsControl={setFormsControl}
          />
        </>
      )}
      <FormActionButtons
        userData={userData}
        formsControl={formsControl}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        fieldData={fieldData}
        fetchUsers={fetchUsers}
      />
    </FormContainer>
  );
}

UserDetailsForm.propTypes = {
  userData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    role: PropTypes.shape({
      name: PropTypes.string
    }),
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    contact_number: PropTypes.number,
    provinces: PropTypes.arrayOf(PropTypes.shape({})),
    channels: PropTypes.arrayOf(PropTypes.shape({})),
    outlet: PropTypes.string,
    cpms_id: PropTypes.string,
    xid: PropTypes.string,
    store_code: PropTypes.string,
    Dealership: PropTypes.shape({
      id: PropTypes.number
    }),
    channelOrgCode: PropTypes.string,
    sales_manager: PropTypes.string,
    sales_manager_email: PropTypes.string,
    formTypes: PropTypes.shape({
      disableBusinessApps: PropTypes.bool,
      disableBusinessConnect: PropTypes.bool,
      disableCallcenter: PropTypes.bool,
      disableIoT: PropTypes.bool
    }),
    isAuthDisabled: PropTypes.bool,
    requiresOrderAccess: PropTypes.string,
    dealershipVisibilityEnabled: PropTypes.bool
  }).isRequired,
  fetchUsers: PropTypes.func.isRequired
};
